import type {
  BuildTiltScoutSnapshotInput,
  TiltScoutSnapshot,
} from "./tilt-scout-types";
import {
  buildProofs,
  confidenceText,
  eventText,
  findActiveWindow,
  invalidationText,
  rangeText,
  scoreLabel,
  toEvidence,
} from "./tilt-scout-copy";
import {
  buildAxisScores,
  clamp,
  roundScore,
  scoreChamber,
  scoreOutlook,
  scoreRiskFlow,
  withOverallAxis,
} from "./tilt-scout-scoring";
import {
  findInstrumentOutlook,
  normalizeSymbol,
  topEvidenceAlerts,
} from "./tilt-scout-symbols";

export function buildTiltScoutSnapshot({
  selectedSymbol,
  outlook,
  alerts,
  dayPlan,
  verdict,
}: BuildTiltScoutSnapshotInput): TiltScoutSnapshot {
  const canonicalSymbol = normalizeSymbol(selectedSymbol);
  const instrument = findInstrumentOutlook(outlook, canonicalSymbol);
  const candidates = topEvidenceAlerts(alerts, canonicalSymbol);
  const topEvidence = toEvidence(candidates[0]);
  const recentRelatedCatalysts = candidates
    .map((alert) => toEvidence(alert))
    .filter(isTiltScoutEvidence)
    .filter(
      (item) =>
        (item.headlineImpactWeight ?? item.headlineWeight ?? item.score) > 7 ||
        item.score > 7,
    )
    .slice(0, 3);
  const activeWindow = findActiveWindow(dayPlan?.windows ?? []);
  const outlookTilt = scoreOutlook(instrument);
  const axisScores = buildAxisScores(candidates);
  const riskTilt = scoreRiskFlow(candidates);
  const chamberTilt = scoreChamber(verdict);
  const score = roundScore(
    clamp(outlookTilt + riskTilt + chamberTilt, -10, 10),
  );
  const posture = score >= 2 ? "bullish" : score <= -2 ? "bearish" : "neutral";

  return {
    selectedSymbol,
    canonicalSymbol,
    score,
    posture,
    label: scoreLabel(score),
    summary: summaryText(score, posture, topEvidence, axisScores),
    confidenceText: confidenceText(instrument, topEvidence, verdict, dayPlan),
    rangeText: rangeText(instrument, topEvidence),
    invalidationText: invalidationText(activeWindow, topEvidence),
    eventText: eventText(activeWindow, dayPlan),
    topEvidence,
    recentRelatedCatalysts,
    proofs: buildProofs({ instrument, topEvidence, verdict, dayPlan }),
    axisScores: withOverallAxis(axisScores, score, candidates.length),
    updatedAt: new Date().toISOString(),
  };
}

function summaryText(
  score: number,
  posture: TiltScoutSnapshot["posture"],
  topEvidence: TiltScoutSnapshot["topEvidence"],
  axisScores: TiltScoutSnapshot["axisScores"],
) {
  const driver = axisScores
    .filter((axis) => axis.id !== "overall" && axis.sourceCount > 0)
    .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))[0];
  const scoreText = `${score > 0 ? "+" : ""}${score.toFixed(1)}`;
  const driverText = driver
    ? `${driver.label.toLowerCase()} pressure is carrying the strongest read`
    : topEvidence
      ? "RiskFlow catalyst pressure is carrying the read"
      : "source coverage is thin";
  const evidenceText = topEvidence?.headlineIvScore
    ? ` with headline IV ${topEvidence.headlineIvScore.toFixed(1)}`
    : "";
  return `TiltScout is ${posture} at ${scoreText} because ${driverText}${evidenceText}.`;
}

function isTiltScoutEvidence(
  item: TiltScoutSnapshot["topEvidence"],
): item is NonNullable<TiltScoutSnapshot["topEvidence"]> {
  return item != null;
}
